import type {
  CategorySelection,
  PillCategories,
  PillCategory,
  PillOption,
  TicketFilterState,
} from 'features/libra/dashboard/ticketDashboard/types'

import type { CardIndex, IndexedCard } from './cardIndex'
import { matchesSelection } from './cardIndex'

export interface FilterOption extends PillOption {
  count: number
  disabled: boolean
}

export interface ResolvedFilter {
  category: string
  label: string
  options: FilterOption[]
}

function countValues(
  rows: IndexedCard[],
  selection: CategorySelection,
  category: string
): Map<string, number> {
  const counts = new Map<string, number>()
  for (const row of rows) {
    if (!matchesSelection(row, selection)) continue
    row.valuesByCategory
      .get(category)
      ?.forEach((value) => counts.set(value, (counts.get(value) ?? 0) + 1))
  }
  return counts
}

function getOptions(
  category: string,
  def: PillCategory,
  index: CardIndex
): PillOption[] {
  const known = def.options ?? []
  const knownValues = new Set(known.map((option) => option.value))
  const extra = [...(index.seen.get(category)?.values() ?? [])].filter(
    (option) => !knownValues.has(option.value)
  )
  return [...known, ...extra]
}

/**
 * Visible rows plus each filter's options. An option's count is how many
 * cards would show if it were picked, given the search and every other filter.
 * Options with no cards are disabled and listed last, unless already selected
 * so they can still be cleared.
 */
export function applyFilters(
  index: CardIndex,
  categories: PillCategories,
  state: TicketFilterState
) {
  const query = state.query.trim().toLowerCase()
  const searchedRows = query
    ? index.rows.filter((row) => row.text.includes(query))
    : index.rows

  const visibleRows = searchedRows.filter((row) =>
    matchesSelection(row, state.selection)
  )
  const filters = Object.entries(categories)
    .filter(([, def]) => def.filter)
    .map(([category, def]) => {
      const counts = countValues(
        searchedRows,
        { ...state.selection, [category]: undefined },
        category
      )
      const selected = new Set(state.selection[category])
      const options = getOptions(category, def, index).map((option) => {
        const count = counts.get(option.value) ?? 0
        return {
          ...option,
          count,
          disabled: count === 0 && !selected.has(option.value),
        }
      })
      return {
        category,
        label: def.label,
        options: [
          ...options.filter((option) => !option.disabled),
          ...options.filter((option) => option.disabled),
        ],
      }
    })

  return { visibleRows, filters }
}
